import { MERCURE_PREFIX, MERCURE_URL } from "../config/_entrypoint";
import { apiGet } from "./Api/apiCall";

let fetchPromise: Promise<void> | null = null;

export const isCookieFetched = () => {
  const auth = Number(sessionStorage.getItem("mercureAuthorization"));
  return !Number.isNaN(auth) && Date.now() - auth < 3600000;
};

export const mercureFetchCookie = async () => {
  if (isCookieFetched() || sessionStorage.getItem("mercureAuthorization") === "fetching") {
    return;
  }

  if (fetchPromise) {
    return fetchPromise;
  }

  //run directly
  fetchPromise = (async () => {
    try {
      sessionStorage.setItem("mercureAuthorization", "fetching");
      await apiGet("mercure/discover", null, {}, { credentials: "include" });
      sessionStorage.setItem("mercureAuthorization", Date.now().toString());
    } catch (error) {
      sessionStorage.setItem("mercureAuthorization", "error");
    } finally {
      fetchPromise = null;
    }
  })();

  return fetchPromise;
};

export const buildTopic = (topic: string) => {
  return "/" + MERCURE_PREFIX + topic;
};

export const mercureSubscribe = <T = unknown>(
  topic: string | string[],
  setNotification: (data: T) => void,
  onOpen?: (e: Event) => void,
  onError?: (e: Event) => void
) => {
  let eventSource: EventSource | null = null;
  let eventSourceClosed = false;
  let retryCount = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  const connect = async () => {
    if (!isCookieFetched()) {
      await mercureFetchCookie();
    }

    let url = new URL(MERCURE_URL as string);

    if (Array.isArray(topic)) {
      topic.forEach((t) => url.searchParams.append("topic", buildTopic(t)));
    } else {
      url.searchParams.append("topic", buildTopic(topic));
    }

    eventSource = new EventSource(url.toString(), { withCredentials: true });

    eventSource.onopen = (e) => {
      retryCount = 0;
      onOpen?.(e);
    };

    eventSource.onmessage = (e) => {
      setNotification(JSON.parse(e.data) as T);
    };

    eventSource.onerror = async (e) => {
      onError?.(e);
      eventSource?.close();

      if (eventSourceClosed) {
        return;
      }

      retryCount++;

      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }

      reconnectTimer = setTimeout(
        async () => {
          sessionStorage.removeItem("mercureAuthorization");
          if (!eventSourceClosed) {
            await connect();
          }
        },
        //retry 1sec,2sec,3sec,... until max 30sec
        Math.min((retryCount + 1) * 1000, 30000)
      );
    };
  };

  connect();

  return () => {
    eventSourceClosed = true;
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
    }
    eventSource?.close();
  };
};
